import { log } from "../../../../common/Logging"
import { ButtonContainer, Buttons } from "../../../generic-sprites/Buttons"
import { ExpandoSprite } from "../../../generic-sprites/ExpandoSprite"
import { Rectangle } from "../../../generic-sprites/Shape"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { MakeAVideoState } from "../MakeAVideoState"
import { MakeAVideoStore } from "../MakeAVideoStore"
import { MakeVideoAtlases, MakeVideoAudio } from "../MakeVideoAssets"
import { MakeVideoMusicButtonType, MakeVideoSprite, MakeVideoSpriteContainer } from "../MakeVideoTypes"

export class MakeVideoMusicButtonContainer implements MakeVideoSprite, MakeVideoSpriteContainer {
    active: boolean
    private nextButton: ButtonContainer
    private buttons: Array<MakeVideoMusicButtonSprite>

    constructor(
        game: Phaser.Game,
        group: Phaser.Group,
        x: number,
        y: number,
        next: ButtonContainer,
        private edit: boolean,
        debug: boolean,
        moveToStep: () => void
    ) {
        this.active = true
        this.nextButton = next
        this.buttons = [
            new MakeVideoMusicButtonSprite(
                game,
                group,
                x,
                y,
                MakeVideoMusicButtonType.COUNTRY,
                this.stopPlayingAudio,
                this.unselectMusicSprites,
                () => this.selectedButton(MakeVideoMusicButtonType.COUNTRY),
                debug
            ),
            new MakeVideoMusicButtonSprite(
                game,
                group,
                x + Scale.x(0.35),
                y,
                MakeVideoMusicButtonType.HIP_HOP,
                this.stopPlayingAudio,
                this.unselectMusicSprites,
                () => this.selectedButton(MakeVideoMusicButtonType.HIP_HOP),
                debug
            ),
            new MakeVideoMusicButtonSprite(
                game,
                group,
                x + Scale.x(0.7),
                y,
                MakeVideoMusicButtonType.POP,
                this.stopPlayingAudio,
                this.unselectMusicSprites,
                () => this.selectedButton(MakeVideoMusicButtonType.POP),
                debug
            )
        ]
        if (this.edit) {
            this.buttons.forEach(button => {
                if (button.audio.key === MakeAVideoStore.instance.music) {
                    button.selected = true
                }
            })
        }
        MakeAVideoState.debugPanel.setSkip(() => {
            game.sound.stopAll()
            moveToStep()
            MakeAVideoStore.instance.music = MakeVideoAudio.COUNTRY
        })
    }

    destroy = () => this.buttons.forEach(button => button.destroy())

    disableAll = () => this.buttons.forEach(button => button.disable())

    enableAll = () => {
        this.buttons.forEach(button => button.enable())
        if (this.edit) {
            this.nextButton.enable()
        }
    }

    private stopPlayingAudio = () => this.buttons.filter(button => button.playing).forEach(button => button.audio.stop())

    private unselectMusicSprites = () => this.buttons.filter(button => !button.audio.isPlaying).forEach(button => button.playing = false)

    private buttonSelected = () => this.buttons.filter(button => button.selected).length > 0

    private selectedButton = (type: MakeVideoMusicButtonType) => {
        if (this.buttonSelected()) {
            this.nextButton.enable()
        } else {
            this.nextButton.disable()
        }
        this.buttons.forEach(button => {
            if (button.type !== type) {
                button.selected = false
            }
        })
    }
}

class MakeVideoMusicButtonSprite implements MakeVideoSprite {
    active: boolean
    audio: Phaser.Sound
    private readonly sprite: ExpandoSprite
    private select: ButtonContainer
    private frame: Rectangle

    constructor(
        private game: Phaser.Game,
        group: Phaser.Group,
        x: number,
        y: number,
        public type: MakeVideoMusicButtonType,
        stopMusic: () => void,
        unselectMusic: () => void,
        onClick: () => void,
        debug: boolean
    ) {
        this.active = true
        let sprite: string
        let music: string
        switch (type) {
            case MakeVideoMusicButtonType.COUNTRY:
                sprite = MakeVideoAtlases.COUNTRY_BUTTON
                music = MakeVideoAudio.COUNTRY
                break
            case MakeVideoMusicButtonType.HIP_HOP:
                sprite = MakeVideoAtlases.HIP_HOP_BUTTON
                music = MakeVideoAudio.HIP_HOP
                break
            case MakeVideoMusicButtonType.POP:
                sprite = MakeVideoAtlases.POP_BUTTON
                music = MakeVideoAudio.POP
                break
            default: log.error("Unrecognized music button type.")
        }
        this.audio = game.add.audio(music)
        this.sprite = new ExpandoSprite(game, x, y, sprite, {scale: 1, debug, group})
        const selectOnClick = () => {
            MakeAVideoStore.instance.music = this.audio.key
            this.selected = true
            onClick()
        }

        this.select = Buttons.create(this.game, type, selectOnClick, {debug, group, x, y})

        this.sprite.addOnClick(() => {
            stopMusic()
            if (this.playing) {
                this.playing = false
            } else {
                this.playing = true
                this.audio.play()
            }
            unselectMusic()
        })

        Sprites.centerSpriteInSprite(this.select.button, this.sprite, true)
        this.select.button.y += (this.sprite.height / 2) + Scale.y(0.05)
        this.select.center()
        this.frame = new Rectangle(game, this.select.button.x, this.select.button.y, this.select.button.width, this.select.button.height, {
            lineWidth: 2,
            lineColor: Phaser.Color.RED
        })
        this.frame.graphics.visible = false
    }

    get playing(): boolean {
        return this.sprite.frame === 1
    }

    set playing(play: boolean) {
        this.sprite.frame = play ? 1 : 0
    }

    get selected(): boolean {
        return this.frame.graphics.visible
    }

    set selected(select: boolean) {
        this.frame.graphics.visible = select
    }

    disable = () => {
        this.sprite.turnOff()
        this.select.disable()
    }

    enable = () => {
        this.sprite.turnOn()
        this.select.enable()
    }

    destroy = () => {
        this.sprite.destroy()
        this.select.destroy()
        this.frame.destroy()
        if (this.audio.isPlaying) {
            this.audio.destroy(true)
        }
        this.active = false
    }
}
